import React, { useState, useEffect } from 'react'
import { PropertiesPanelProps, CollapsedSections } from './types'
import { SettingsPanel } from './components/SettingsPanel'
import { WallProperties } from './components/WallProperties'
import { DoorProperties } from './components/DoorProperties'
import { WindowProperties } from './components/WindowProperties'
import {
  PolygonProperties,
  CurvedEdgesProperties,
} from './components/PolygonProperties'
import { StairsProperties } from './components/StairsProperties'
import { PoiProperties } from './components/PoiProperties'
import { TextProperties } from './components/TextProperties'
import { PositionSection } from './components/PositionSection'
import { AppearanceSection } from './components/AppearanceSection'
import { QuickActionsSection } from './components/QuickActionsSection'
import { FloorLayerSection } from './components/FloorLayerSection'
import {
  PanelContainer,
  PanelHeader,
  HeaderTitle,
  PropertiesContent,
  EmptyState,
  EmptyStateIcon,
  PropertySection,
  SectionHeader,
  SectionTitle,
  CollapseIcon,
  SectionContent,
  PropertyGroup,
  Label,
  Input,
  InfoText,
  RangeInput,
  RangeValue,
  FlexRow,
} from './styled'
import { getUnitSingular } from './helpers'

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedElement,
  onUpdateElement,
  onDeleteElement,
  onAddElement,
  mapSettings,
  showSettingsPanel = false,
  onSettingsChange,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [localProperties, setLocalProperties] = useState<Record<string, any>>(
    {},
  )
  const [collapsedSections, setCollapsedSections] = useState<CollapsedSections>(
    {
      position: false,
      properties: false,
      curvedEdges: false,
      floorLayer: false,
      appearance: false,
      actions: false,
      general: false,
      background: false,
      canvasBackground: false,
    },
  )

  const toggleSection = (section: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section as keyof CollapsedSections],
    }))
  }

  useEffect(() => {
    if (selectedElement) {
      setLocalProperties(selectedElement.properties)
    }
  }, [selectedElement])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateProperty = (key: string, value: any) => {
    if (!selectedElement) return

    const updates = { properties: { ...localProperties, [key]: value } }
    setLocalProperties(prev => ({ ...prev, [key]: value }))
    onUpdateElement(selectedElement.id, updates)
  }

  const updatePosition = (x?: number, y?: number) => {
    if (!selectedElement) return

    const updates = {
      position: {
        x: x !== undefined ? x : selectedElement.position.x,
        y: y !== undefined ? y : selectedElement.position.y,
      },
    }
    onUpdateElement(selectedElement.id, updates)
  }

  const updateMeasurement = (key: string, value: number) => {
    if (!selectedElement) return

    const updates = {
      measurements: {
        ...selectedElement.measurements,
        [key]: value,
      },
    }
    onUpdateElement(selectedElement.id, updates)
  }

  if (showSettingsPanel) {
    return (
      <SettingsPanel
        mapSettings={mapSettings}
        onSettingsChange={onSettingsChange}
        collapsedSections={{
          general: collapsedSections.general,
          background: collapsedSections.background,
          canvasBackground: collapsedSections.canvasBackground,
        }}
        toggleSection={toggleSection}
      />
    )
  }

  if (!selectedElement) {
    return (
      <PanelContainer>
        <PanelHeader>
          <HeaderTitle>Properties</HeaderTitle>
        </PanelHeader>
        <EmptyState>
          <EmptyStateIcon>🎯</EmptyStateIcon>
          <div>
            <strong>No element selected</strong>
            <br />
            Select an element to edit its properties
          </div>
        </EmptyState>
      </PanelContainer>
    )
  }

  const renderElementSpecificProperties = () => {
    switch (selectedElement.type) {
      case 'wall':
        return (
          <WallProperties
            selectedElement={selectedElement}
            mapSettings={mapSettings}
            localProperties={localProperties}
            isCollapsed={collapsedSections.properties}
            onToggleSection={() => toggleSection('properties')}
            updateProperty={updateProperty}
            updateMeasurement={updateMeasurement}
            onUpdateElement={onUpdateElement}
          />
        )

      case 'door':
        return (
          <DoorProperties
            selectedElement={selectedElement}
            mapSettings={mapSettings}
            localProperties={localProperties}
            isCollapsed={collapsedSections.properties}
            onToggleSection={() => toggleSection('properties')}
            updateProperty={updateProperty}
            updateMeasurement={updateMeasurement}
            onUpdateElement={onUpdateElement}
          />
        )

      case 'window':
        return (
          <WindowProperties
            selectedElement={selectedElement}
            mapSettings={mapSettings}
            localProperties={localProperties}
            isCollapsed={collapsedSections.properties}
            onToggleSection={() => toggleSection('properties')}
            updateProperty={updateProperty}
            updateMeasurement={updateMeasurement}
            onUpdateElement={onUpdateElement}
          />
        )

      case 'room':
      case 'polygon':
        return (
          <>
            <PolygonProperties
              selectedElement={selectedElement}
              mapSettings={mapSettings}
              localProperties={localProperties}
              isCollapsed={collapsedSections.properties}
              onToggleSection={() => toggleSection('properties')}
              updateProperty={updateProperty}
              updateMeasurement={updateMeasurement}
            />
            {selectedElement.type === 'polygon' && (
              <CurvedEdgesProperties
                selectedElement={selectedElement}
                mapSettings={mapSettings}
                isCollapsed={collapsedSections.curvedEdges}
                onToggleSection={() => toggleSection('curvedEdges')}
                onUpdateElement={onUpdateElement}
              />
            )}
          </>
        )

      case 'stairs':
        return (
          <StairsProperties
            selectedElement={selectedElement}
            mapSettings={mapSettings}
            localProperties={localProperties}
            isCollapsed={collapsedSections.properties}
            onToggleSection={() => toggleSection('properties')}
            updateProperty={updateProperty}
            onUpdateElement={onUpdateElement}
          />
        )

      case 'poi':
        return (
          <PoiProperties
            selectedElement={selectedElement}
            localProperties={localProperties}
            isCollapsed={collapsedSections.properties}
            onToggleSection={() => toggleSection('properties')}
            updateProperty={updateProperty}
          />
        )

      case 'text':
        return (
          <TextProperties
            selectedElement={selectedElement}
            localProperties={localProperties}
            isCollapsed={collapsedSections.properties}
            onToggleSection={() => toggleSection('properties')}
            updateProperty={updateProperty}
          />
        )

      case 'fixture-rect':
      case 'fixture-line':
      case 'fixture-point':
      case 'fixture-circle':
      case 'fixture-polygon':
        // TODO: Create proper FixtureProperties component
        return renderSimpleFixtureProperties()

      case 'line':
      case 'rectangle':
      case 'circle':
      case 'measurement':
        // TODO: Create proper ShapeProperties component
        return renderSimpleShapeProperties()

      default:
        return null
    }
  }

  // Temporary simple renderers - should be replaced with proper components
  const renderSimpleFixtureProperties = () => (
    <PropertySection>
      <SectionHeader
        isCollapsed={collapsedSections.properties}
        onClick={() => toggleSection('properties')}>
        <SectionTitle>Fixture Properties</SectionTitle>
        <CollapseIcon isCollapsed={collapsedSections.properties}>
          ▼
        </CollapseIcon>
      </SectionHeader>
      <SectionContent isCollapsed={collapsedSections.properties}>
        <PropertyGroup>
          <Label>Fixture Type</Label>
          <Input
            value={localProperties.fixtureType || ''}
            onChange={e => updateProperty('fixtureType', e.target.value)}
            placeholder="Enter fixture type"
          />
        </PropertyGroup>
        {selectedElement.type === 'fixture-point' && (
          <PropertyGroup>
            <Label>Size</Label>
            <FlexRow>
              <RangeInput
                min="10"
                max="50"
                value={localProperties.size || 20}
                onChange={e => updateProperty('size', Number(e.target.value))}
              />
              <RangeValue>{localProperties.size || 20}px</RangeValue>
            </FlexRow>
          </PropertyGroup>
        )}
      </SectionContent>
    </PropertySection>
  )

  const renderSimpleShapeProperties = () => (
    <PropertySection>
      <SectionHeader
        isCollapsed={collapsedSections.properties}
        onClick={() => toggleSection('properties')}>
        <SectionTitle>Shape Properties</SectionTitle>
        <CollapseIcon isCollapsed={collapsedSections.properties}>
          ▼
        </CollapseIcon>
      </SectionHeader>
      <SectionContent isCollapsed={collapsedSections.properties}>
        {selectedElement.type === 'line' && (
          <PropertyGroup>
            <Label>Length ({getUnitSingular(mapSettings)})</Label>
            <Input
              type="number"
              step="0.1"
              value={(selectedElement.measurements?.realLength || 0).toFixed(1)}
              disabled
            />
            <InfoText>Calculated from endpoints</InfoText>
          </PropertyGroup>
        )}
      </SectionContent>
    </PropertySection>
  )

  return (
    <PanelContainer>
      <PanelHeader>
        <HeaderTitle>Properties</HeaderTitle>
      </PanelHeader>

      <PropertiesContent>
        {/* Position Section */}
        <PositionSection
          selectedElement={selectedElement}
          isCollapsed={collapsedSections.position}
          onToggleSection={() => toggleSection('position')}
          updatePosition={updatePosition}
        />

        {/* Element-specific properties */}
        {renderElementSpecificProperties()}

        {/* Floor Assignment Section */}
        <FloorLayerSection
          selectedElement={selectedElement}
          mapSettings={mapSettings}
          isCollapsed={collapsedSections.floorLayer}
          onToggleSection={() => toggleSection('floorLayer')}
          onUpdateElement={onUpdateElement}
        />

        {/* Appearance Section */}
        <AppearanceSection
          selectedElement={selectedElement}
          localProperties={localProperties}
          isCollapsed={collapsedSections.appearance}
          onToggleSection={() => toggleSection('appearance')}
          updateProperty={updateProperty}
        />

        {/* Quick Actions Section */}
        <QuickActionsSection
          selectedElement={selectedElement}
          isCollapsed={collapsedSections.actions}
          onToggleSection={() => toggleSection('actions')}
          onUpdateElement={onUpdateElement}
          onDeleteElement={onDeleteElement}
          onAddElement={onAddElement}
        />
      </PropertiesContent>
    </PanelContainer>
  )
}
export { PropertiesPanel }
